// Requirements catalog — the per-jurisdiction reference data the gap-analysis
// engine resolves against. A jurisdiction select drives three stacked sections
// (license types, requirements, reciprocity) for the chosen jurisdiction, each
// with create / edit and a Verify action that stamps last_verified_at /
// verified_at. Below: a read-only regulators card and the company-held
// training-cert section.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  ComplianceLicenseType,
  ComplianceRequirement,
  ComplianceTrainingCert,
  ReciprocityAgreement,
  RegulatorKind,
  RequirementKind,
  RequirementSeverity,
  ReciprocityStatus,
  ServiceLine,
  LicenseScope,
  RenewalBasis,
  TrainingCertKind,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DocumentAttachment } from '../components/DocumentAttachment';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  Card,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Textarea,
  type BadgeTone,
} from '../components/ui';
import { ApiError } from '../api/client';
import {
  useJurisdictions,
  useRegulators,
  useLicenseTypes,
  useRequirements,
  useReciprocity,
  useTrainingCerts,
  useCreateLicenseType,
  useUpdateLicenseType,
  useVerifyLicenseType,
  useCreateRequirement,
  useUpdateRequirement,
  useVerifyRequirement,
  useCreateReciprocity,
  useUpdateReciprocity,
  useVerifyReciprocity,
  useCreateTrainingCert,
  useUpdateTrainingCert,
} from '../api/queries/compliance';

// ---------- Static option lists ----------

const SERVICE_LINES: { value: ServiceLine; label: string }[] = [
  { value: 'unarmed_guard', label: 'Unarmed guard' },
  { value: 'armed_guard', label: 'Armed guard' },
  { value: 'executive_protection', label: 'Executive protection' },
  { value: 'private_investigation', label: 'Private investigation' },
];

const LICENSE_SCOPES: { value: LicenseScope; label: string }[] = [
  { value: 'company', label: 'Company' },
  { value: 'branch', label: 'Branch' },
  { value: 'individual', label: 'Individual' },
  { value: 'endorsement', label: 'Endorsement' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'school', label: 'School' },
];

const RENEWAL_BASES: { value: RenewalBasis; label: string }[] = [
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'fixed_date', label: 'Fixed date' },
];

const REQUIREMENT_KINDS: { value: RequirementKind; label: string }[] = [
  { value: 'company_license', label: 'Company license' },
  { value: 'branch_license', label: 'Branch license' },
  { value: 'employee_registration', label: 'Employee registration' },
  { value: 'qualifying_agent', label: 'Qualifying agent' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'bond', label: 'Bond' },
  { value: 'firearm_endorsement', label: 'Firearm endorsement' },
  { value: 'training_cert', label: 'Training cert' },
  { value: 'advertising_rule', label: 'Advertising rule' },
  { value: 'uniform_vehicle_rule', label: 'Uniform / vehicle rule' },
  { value: 'local_license', label: 'Local license' },
  { value: 'foreign_qualification', label: 'Foreign qualification' },
  { value: 'none_required', label: 'None required' },
  { value: 'other', label: 'Other' },
];

const REQUIREMENT_SEVERITIES: { value: RequirementSeverity; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'standard', label: 'Standard' },
  { value: 'license_critical', label: 'License critical' },
];

const RECIPROCITY_STATUSES: { value: ReciprocityStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'terminated', label: 'Terminated' },
];

const REGULATOR_KINDS: { value: RegulatorKind; label: string }[] = [
  { value: 'state_police', label: 'State police' },
  { value: 'consumer_affairs', label: 'Consumer affairs' },
  { value: 'sos_board', label: 'SoS / board' },
  { value: 'criminal_justice', label: 'Criminal justice' },
  { value: 'court', label: 'Court' },
  { value: 'police_dept', label: 'Police department' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'other', label: 'Other' },
];

const TRAINING_CERT_KINDS: { value: TrainingCertKind; label: string }[] = [
  { value: 'instructor', label: 'Instructor' },
  { value: 'school', label: 'School' },
  { value: 'firearms_facility', label: 'Firearms facility' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null | undefined): string {
  return epochMs === null || epochMs === undefined
    ? '—'
    : new Date(epochMs).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
}

function serviceLineLabel(line: ServiceLine): string {
  return SERVICE_LINES.find((s) => s.value === line)?.label ?? line;
}

function requirementKindLabel(kind: RequirementKind): string {
  return REQUIREMENT_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

function severityTone(sev: RequirementSeverity): BadgeTone {
  switch (sev) {
    case 'license_critical':
      return 'danger';
    case 'standard':
      return 'warn';
    default:
      return 'neutral';
  }
}

function reciprocityStatusTone(status: ReciprocityStatus): BadgeTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warn';
    default:
      return 'neutral';
  }
}

function regulatorKindLabel(kind: RegulatorKind): string {
  return REGULATOR_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

function trainingCertKindLabel(kind: TrainingCertKind): string {
  return TRAINING_CERT_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

// =============================================================================
// Page
// =============================================================================

export function RequirementsCatalogPage(): JSX.Element {
  const jurisdictionsQuery = useJurisdictions();
  const jurisdictions = jurisdictionsQuery.data?.jurisdictions ?? [];

  const [jurisdictionId, setJurisdictionId] = useState<number | ''>('');

  // Default to the first jurisdiction once the list loads.
  const effectiveJurisdiction =
    jurisdictionId === '' && jurisdictions.length > 0 ? jurisdictions[0]!.id : jurisdictionId;

  const jurisdictionName = useMemo(() => {
    const map = new Map<number, string>();
    for (const j of jurisdictions) map.set(j.id, j.name);
    return (id: number): string => map.get(id) ?? `Jurisdiction #${id}`;
  }, [jurisdictions]);

  return (
    <section className="compliance-page">
      <header className="page-header">
        <div>
          <h2>Requirements catalog</h2>
          <p className="page-sub">
            Per-jurisdiction reference data the gap-analysis engine resolves against.
          </p>
        </div>
      </header>

      <div className="page-toolbar">
        <FormField label="Jurisdiction" className="inline">
          <select
            className="form-input"
            value={effectiveJurisdiction === '' ? '' : String(effectiveJurisdiction)}
            onChange={(e) => setJurisdictionId(e.target.value ? Number(e.target.value) : '')}
          >
            {jurisdictionsQuery.isPending && <option value="">Loading…</option>}
            {!jurisdictionsQuery.isPending && jurisdictions.length === 0 && (
              <option value="">No jurisdictions</option>
            )}
            {jurisdictions.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}{j.code ? ` (${j.code})` : ''}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      {jurisdictionsQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(jurisdictionsQuery.error, 'Failed to load jurisdictions.')}
        </p>
      )}

      {effectiveJurisdiction !== '' && (
        <>
          <LicenseTypesSection jurisdictionId={effectiveJurisdiction} />
          <RequirementsSection jurisdictionId={effectiveJurisdiction} />
          <ReciprocitySection
            jurisdictionId={effectiveJurisdiction}
            jurisdictionName={jurisdictionName}
          />
        </>
      )}

      <RegulatorsCard />
      <TrainingCertsSection jurisdictionName={jurisdictionName} />
    </section>
  );
}

// =============================================================================
// License types section
// =============================================================================

function LicenseTypesSection({ jurisdictionId }: { jurisdictionId: number }): JSX.Element {
  const canWrite = can('compliance.write');
  const canVerify = can('compliance.verify');

  const query = useLicenseTypes({ jurisdiction_id: jurisdictionId });
  const verify = useVerifyLicenseType();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ComplianceLicenseType | null>(null);

  const types = query.data?.license_types ?? [];

  const onVerify = async (id: number): Promise<void> => {
    try {
      await verify.mutateAsync(id);
      toast.success('License type verified.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not verify the license type.'));
    }
  };

  return (
    <section className="card compliance-card">
      <header className="card-header">
        <div><h3>License types</h3></div>
        {canWrite && (
          <Button variant="ghost" className="btn-sm" onClick={() => setCreateOpen(true)}>
            Add license type
          </Button>
        )}
      </header>

      {query.isPending && <p className="muted">Loading license types…</p>}
      {query.isError && (
        <p className="error-text" role="alert">
          {errMessage(query.error, 'Failed to load license types.')}
        </p>
      )}
      {!query.isPending && !query.isError && types.length === 0 && (
        <p className="muted empty-state">No license types for this jurisdiction.</p>
      )}

      {types.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Code</Table.HeaderCell>
              <Table.HeaderCell>Display name</Table.HeaderCell>
              <Table.HeaderCell>Scope</Table.HeaderCell>
              <Table.HeaderCell>Service lines</Table.HeaderCell>
              <Table.HeaderCell>Last verified</Table.HeaderCell>
              {(canWrite || canVerify) && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {types.map((lt) => (
              <Table.Row key={lt.id}>
                <Table.Cell tone="mono">{lt.code}</Table.Cell>
                <Table.Cell tone="strong">{lt.display_name}</Table.Cell>
                <Table.Cell>{LICENSE_SCOPES.find((s) => s.value === lt.scope)?.label ?? lt.scope}</Table.Cell>
                <Table.Cell tone="muted">{lt.service_lines.map(serviceLineLabel).join(', ')}</Table.Cell>
                <Table.Cell>{fmtDate(lt.last_verified_at)}</Table.Cell>
                {(canWrite || canVerify) && (
                  <Table.Cell className="col-actions">
                    {canWrite && (
                      <Button variant="ghost" className="btn-sm" onClick={() => setEditing(lt)}>
                        Edit
                      </Button>
                    )}
                    {canVerify && (
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => onVerify(lt.id)}
                        disabled={verify.isPending}
                      >
                        Verify
                      </Button>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <LicenseTypeModal mode="create" jurisdictionId={jurisdictionId} onClose={() => setCreateOpen(false)} />
      )}
      {editing && (
        <LicenseTypeModal mode="edit" jurisdictionId={jurisdictionId} licenseType={editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

interface LicenseTypeModalProps {
  mode: 'create' | 'edit';
  jurisdictionId: number;
  licenseType?: ComplianceLicenseType;
  onClose: () => void;
}

function LicenseTypeModal({ mode, jurisdictionId, licenseType, onClose }: LicenseTypeModalProps): JSX.Element {
  const create = useCreateLicenseType();
  const update = useUpdateLicenseType();
  const busy = create.isPending || update.isPending;

  const [code, setCode] = useState(licenseType?.code ?? '');
  const [displayName, setDisplayName] = useState(licenseType?.display_name ?? '');
  const [scope, setScope] = useState<LicenseScope>(licenseType?.scope ?? 'company');
  const [renewalBasis, setRenewalBasis] = useState<RenewalBasis>(licenseType?.renewal_basis ?? 'anniversary');
  const [regulatorId, setRegulatorId] = useState(licenseType ? String(licenseType.regulator_id) : '');
  const [termMonths, setTermMonths] = useState(
    licenseType?.term_months !== null && licenseType?.term_months !== undefined ? String(licenseType.term_months) : '',
  );
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>(licenseType?.service_lines ?? []);
  const [statuteRef, setStatuteRef] = useState(licenseType?.statute_ref ?? '');
  const [sourceUrl, setSourceUrl] = useState(licenseType?.source_url ?? '');
  const [error, setError] = useState<string | null>(null);

  const toggleLine = (line: ServiceLine): void => {
    setServiceLines((cur) =>
      cur.includes(line) ? cur.filter((l) => l !== line) : [...cur, line],
    );
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (code.trim() === '' || displayName.trim() === '') {
      setError('Code and display name are required.');
      return;
    }
    const regId = Number(regulatorId);
    if (mode === 'create' && (!Number.isInteger(regId) || regId <= 0)) {
      setError('A regulator id is required.');
      return;
    }
    const term = termMonths.trim() === '' ? undefined : Number(termMonths);
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          jurisdiction_id: jurisdictionId,
          regulator_id: regId,
          code: code.trim(),
          display_name: displayName.trim(),
          scope,
          service_lines: serviceLines,
          renewal_basis: renewalBasis,
          ...(term !== undefined ? { term_months: term } : {}),
          ...(statuteRef.trim() ? { statute_ref: statuteRef.trim() } : {}),
          ...(sourceUrl.trim() ? { source_url: sourceUrl.trim() } : {}),
        });
        toast.success('License type created.');
      } else if (licenseType) {
        await update.mutateAsync({
          id: licenseType.id,
          regulator_id: regId,
          code: code.trim(),
          display_name: displayName.trim(),
          scope,
          service_lines: serviceLines,
          renewal_basis: renewalBasis,
          term_months: term ?? null,
          statute_ref: statuteRef.trim() ? statuteRef.trim() : null,
          source_url: sourceUrl.trim() ? sourceUrl.trim() : null,
        });
        toast.success('License type updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the license type.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Add license type' : 'Edit license type'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="license-type-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="license-type-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Code" required>
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={60}
              disabled={busy}
              required
              autoFocus
              placeholder="e.g. PPO"
            />
          </FormField>
          <FormField label="Regulator id" required>
            <Input
              type="number"
              value={regulatorId}
              onChange={(e) => setRegulatorId(e.target.value)}
              min={1}
              disabled={busy}
              required
            />
          </FormField>
        </FormRow>
        <FormField label="Display name" required>
          <Input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={200}
            disabled={busy}
            required
          />
        </FormField>
        <FormRow>
          <FormField label="Scope">
            <select
              className="form-input"
              value={scope}
              onChange={(e) => setScope(e.target.value as LicenseScope)}
              disabled={busy}
            >
              {LICENSE_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Renewal basis">
            <select
              className="form-input"
              value={renewalBasis}
              onChange={(e) => setRenewalBasis(e.target.value as RenewalBasis)}
              disabled={busy}
            >
              {RENEWAL_BASES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormField label="Term (months)">
          <Input
            type="number"
            value={termMonths}
            onChange={(e) => setTermMonths(e.target.value)}
            min={1}
            disabled={busy}
          />
        </FormField>
        <fieldset className="form-field">
          <span>Service lines</span>
          <div className="checkbox-row">
            {SERVICE_LINES.map((s) => (
              <label key={s.value} className="checkbox-field">
                <input
                  type="checkbox"
                  checked={serviceLines.includes(s.value)}
                  onChange={() => toggleLine(s.value)}
                  disabled={busy}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <FormRow>
          <FormField label="Statute ref">
            <Input
              type="text"
              value={statuteRef}
              onChange={(e) => setStatuteRef(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
          <FormField label="Source URL">
            <Input
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              maxLength={2000}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// Requirements section
// =============================================================================

function RequirementsSection({ jurisdictionId }: { jurisdictionId: number }): JSX.Element {
  const canWrite = can('compliance.write');
  const canVerify = can('compliance.verify');

  const query = useRequirements({ jurisdiction_id: jurisdictionId });
  const verify = useVerifyRequirement();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ComplianceRequirement | null>(null);

  const requirements = query.data?.requirements ?? [];

  const onVerify = async (id: number): Promise<void> => {
    try {
      await verify.mutateAsync(id);
      toast.success('Requirement verified.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not verify the requirement.'));
    }
  };

  return (
    <section className="card compliance-card">
      <header className="card-header">
        <div><h3>Requirements</h3></div>
        {canWrite && (
          <Button variant="ghost" className="btn-sm" onClick={() => setCreateOpen(true)}>
            Add requirement
          </Button>
        )}
      </header>

      {query.isPending && <p className="muted">Loading requirements…</p>}
      {query.isError && (
        <p className="error-text" role="alert">
          {errMessage(query.error, 'Failed to load requirements.')}
        </p>
      )}
      {!query.isPending && !query.isError && requirements.length === 0 && (
        <p className="muted empty-state">No requirements for this jurisdiction.</p>
      )}

      {requirements.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Service line</Table.HeaderCell>
              <Table.HeaderCell>Kind</Table.HeaderCell>
              <Table.HeaderCell>Severity</Table.HeaderCell>
              <Table.HeaderCell>Summary</Table.HeaderCell>
              <Table.HeaderCell>Last verified</Table.HeaderCell>
              {(canWrite || canVerify) && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {requirements.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell>{serviceLineLabel(r.service_line)}</Table.Cell>
                <Table.Cell><Badge tone="neutral">{requirementKindLabel(r.kind)}</Badge></Table.Cell>
                <Table.Cell>
                  <Badge tone={severityTone(r.severity)}>
                    {REQUIREMENT_SEVERITIES.find((s) => s.value === r.severity)?.label ?? r.severity}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="cell-truncate">{r.summary}</Table.Cell>
                <Table.Cell>{fmtDate(r.last_verified_at)}</Table.Cell>
                {(canWrite || canVerify) && (
                  <Table.Cell className="col-actions">
                    {canWrite && (
                      <Button variant="ghost" className="btn-sm" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                    )}
                    {canVerify && (
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => onVerify(r.id)}
                        disabled={verify.isPending}
                      >
                        Verify
                      </Button>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <RequirementModal mode="create" jurisdictionId={jurisdictionId} onClose={() => setCreateOpen(false)} />
      )}
      {editing && (
        <RequirementModal mode="edit" jurisdictionId={jurisdictionId} requirement={editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

interface RequirementModalProps {
  mode: 'create' | 'edit';
  jurisdictionId: number;
  requirement?: ComplianceRequirement;
  onClose: () => void;
}

function RequirementModal({ mode, jurisdictionId, requirement, onClose }: RequirementModalProps): JSX.Element {
  const create = useCreateRequirement();
  const update = useUpdateRequirement();
  const busy = create.isPending || update.isPending;

  const [serviceLine, setServiceLine] = useState<ServiceLine>(requirement?.service_line ?? 'unarmed_guard');
  const [kind, setKind] = useState<RequirementKind>(requirement?.kind ?? 'company_license');
  const [severity, setSeverity] = useState<RequirementSeverity>(requirement?.severity ?? 'standard');
  const [summary, setSummary] = useState(requirement?.summary ?? '');
  const [statuteRef, setStatuteRef] = useState(requirement?.statute_ref ?? '');
  const [sourceUrl, setSourceUrl] = useState(requirement?.source_url ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (summary.trim() === '') {
      setError('Summary is required.');
      return;
    }
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          jurisdiction_id: jurisdictionId,
          service_line: serviceLine,
          kind,
          summary: summary.trim(),
          severity,
          ...(statuteRef.trim() ? { statute_ref: statuteRef.trim() } : {}),
          ...(sourceUrl.trim() ? { source_url: sourceUrl.trim() } : {}),
        });
        toast.success('Requirement created.');
      } else if (requirement) {
        await update.mutateAsync({
          id: requirement.id,
          service_line: serviceLine,
          kind,
          summary: summary.trim(),
          severity,
          statute_ref: statuteRef.trim() ? statuteRef.trim() : null,
          source_url: sourceUrl.trim() ? sourceUrl.trim() : null,
        });
        toast.success('Requirement updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the requirement.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Add requirement' : 'Edit requirement'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="requirement-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="requirement-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Service line">
            <select
              className="form-input"
              value={serviceLine}
              onChange={(e) => setServiceLine(e.target.value as ServiceLine)}
              disabled={busy}
            >
              {SERVICE_LINES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Kind">
            <select
              className="form-input"
              value={kind}
              onChange={(e) => setKind(e.target.value as RequirementKind)}
              disabled={busy}
            >
              {REQUIREMENT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormField label="Severity">
          <select
            className="form-input"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as RequirementSeverity)}
            disabled={busy}
          >
            {REQUIREMENT_SEVERITIES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Summary" required>
          <Textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            maxLength={4000}
            disabled={busy}
            required
          />
        </FormField>
        <FormRow>
          <FormField label="Statute ref">
            <Input
              type="text"
              value={statuteRef}
              onChange={(e) => setStatuteRef(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
          <FormField label="Source URL">
            <Input
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              maxLength={2000}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// Reciprocity section — both directions (this jurisdiction as home or host)
// =============================================================================

function ReciprocitySection({
  jurisdictionId,
  jurisdictionName,
}: {
  jurisdictionId: number;
  jurisdictionName: (id: number) => string;
}): JSX.Element {
  const canWrite = can('compliance.write');
  const canVerify = can('compliance.verify');

  // The filter only narrows by home OR host id, so to show BOTH directions for
  // this jurisdiction we fetch unfiltered and match either side client-side.
  const query = useReciprocity();
  const verify = useVerifyReciprocity();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ReciprocityAgreement | null>(null);

  const agreements = (query.data?.reciprocity_agreements ?? []).filter(
    (a) =>
      a.home_jurisdiction_id === jurisdictionId ||
      a.host_jurisdiction_id === jurisdictionId,
  );

  const onVerify = async (id: number): Promise<void> => {
    try {
      await verify.mutateAsync(id);
      toast.success('Reciprocity verified.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not verify the agreement.'));
    }
  };

  return (
    <section className="card compliance-card">
      <header className="card-header">
        <div><h3>Reciprocity</h3></div>
        {canWrite && (
          <Button variant="ghost" className="btn-sm" onClick={() => setCreateOpen(true)}>
            Add agreement
          </Button>
        )}
      </header>

      {query.isPending && <p className="muted">Loading reciprocity…</p>}
      {query.isError && (
        <p className="error-text" role="alert">
          {errMessage(query.error, 'Failed to load reciprocity agreements.')}
        </p>
      )}
      {!query.isPending && !query.isError && agreements.length === 0 && (
        <p className="muted empty-state">No reciprocity agreements for this jurisdiction.</p>
      )}

      {agreements.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Direction</Table.HeaderCell>
              <Table.HeaderCell>Home → Host</Table.HeaderCell>
              <Table.HeaderCell>Service line</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Verified</Table.HeaderCell>
              {(canWrite || canVerify) && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {agreements.map((a) => {
              const isHome = a.home_jurisdiction_id === jurisdictionId;
              return (
                <Table.Row key={a.id}>
                  <Table.Cell>
                    <Badge tone="neutral">{isHome ? 'Outbound' : 'Inbound'}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {a.home_jurisdiction_name ?? a.home_jurisdiction_code ?? jurisdictionName(a.home_jurisdiction_id)}
                    {' → '}
                    {a.host_jurisdiction_name ?? a.host_jurisdiction_code ?? jurisdictionName(a.host_jurisdiction_id)}
                  </Table.Cell>
                  <Table.Cell>{serviceLineLabel(a.service_line)}</Table.Cell>
                  <Table.Cell>
                    <Badge tone={reciprocityStatusTone(a.status)}>
                      {RECIPROCITY_STATUSES.find((s) => s.value === a.status)?.label ?? a.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{fmtDate(a.verified_at)}</Table.Cell>
                  {(canWrite || canVerify) && (
                    <Table.Cell className="col-actions">
                      {canWrite && (
                        <Button variant="ghost" className="btn-sm" onClick={() => setEditing(a)}>
                          Edit
                        </Button>
                      )}
                      {canVerify && (
                        <Button
                          variant="ghost"
                          className="btn-sm"
                          onClick={() => onVerify(a.id)}
                          disabled={verify.isPending}
                        >
                          Verify
                        </Button>
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <ReciprocityModal mode="create" homeJurisdictionId={jurisdictionId} onClose={() => setCreateOpen(false)} />
      )}
      {editing && (
        <ReciprocityModal mode="edit" homeJurisdictionId={jurisdictionId} agreement={editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

interface ReciprocityModalProps {
  mode: 'create' | 'edit';
  homeJurisdictionId: number;
  agreement?: ReciprocityAgreement;
  onClose: () => void;
}

function ReciprocityModal({ mode, homeJurisdictionId, agreement, onClose }: ReciprocityModalProps): JSX.Element {
  const create = useCreateReciprocity();
  const update = useUpdateReciprocity();
  const busy = create.isPending || update.isPending;

  const [homeId, setHomeId] = useState(
    agreement ? String(agreement.home_jurisdiction_id) : String(homeJurisdictionId),
  );
  const [hostId, setHostId] = useState(agreement ? String(agreement.host_jurisdiction_id) : '');
  const [serviceLine, setServiceLine] = useState<ServiceLine>(agreement?.service_line ?? 'unarmed_guard');
  const [status, setStatus] = useState<ReciprocityStatus>(agreement?.status ?? 'active');
  const [scope, setScope] = useState(agreement?.scope ?? '');
  const [durationLimit, setDurationLimit] = useState(agreement?.duration_limit ?? '');
  const [sourceUrl, setSourceUrl] = useState(agreement?.source_url ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const home = Number(homeId);
    const host = Number(hostId);
    if (!Number.isInteger(home) || home <= 0 || !Number.isInteger(host) || host <= 0) {
      setError('Home and host jurisdiction ids are required.');
      return;
    }
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          home_jurisdiction_id: home,
          host_jurisdiction_id: host,
          service_line: serviceLine,
          status,
          ...(scope.trim() ? { scope: scope.trim() } : {}),
          ...(durationLimit.trim() ? { duration_limit: durationLimit.trim() } : {}),
          ...(sourceUrl.trim() ? { source_url: sourceUrl.trim() } : {}),
        });
        toast.success('Reciprocity agreement created.');
      } else if (agreement) {
        await update.mutateAsync({
          id: agreement.id,
          home_jurisdiction_id: home,
          host_jurisdiction_id: host,
          service_line: serviceLine,
          status,
          scope: scope.trim() ? scope.trim() : null,
          duration_limit: durationLimit.trim() ? durationLimit.trim() : null,
          source_url: sourceUrl.trim() ? sourceUrl.trim() : null,
        });
        toast.success('Reciprocity agreement updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the agreement.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Add reciprocity agreement' : 'Edit reciprocity agreement'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="reciprocity-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="reciprocity-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Home jurisdiction id" required>
            <Input
              type="number"
              value={homeId}
              onChange={(e) => setHomeId(e.target.value)}
              min={1}
              disabled={busy}
              required
            />
          </FormField>
          <FormField label="Host jurisdiction id" required>
            <Input
              type="number"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
              min={1}
              disabled={busy}
              required
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Service line">
            <select
              className="form-input"
              value={serviceLine}
              onChange={(e) => setServiceLine(e.target.value as ServiceLine)}
              disabled={busy}
            >
              {SERVICE_LINES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as ReciprocityStatus)}
              disabled={busy}
            >
              {RECIPROCITY_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormField label="Scope">
          <Input
            type="text"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            maxLength={300}
            disabled={busy}
          />
        </FormField>
        <FormRow>
          <FormField label="Duration limit">
            <Input
              type="text"
              value={durationLimit}
              onChange={(e) => setDurationLimit(e.target.value)}
              maxLength={120}
              disabled={busy}
              placeholder="e.g. 30 days"
            />
          </FormField>
          <FormField label="Source URL">
            <Input
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              maxLength={2000}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// Regulators card — read-only list
//   The compliance queries contract exposes no regulator create/update hooks,
//   so this card is presentation-only; regulators are seeded via the catalog
//   data pipeline. (Editing UI is a future addition once mutations land.)
// =============================================================================

function RegulatorsCard(): JSX.Element {
  const query = useRegulators();
  const regulators = query.data?.regulators ?? [];

  return (
    <section className="card compliance-card">
      <header className="card-header"><h3>Regulators</h3></header>

      {query.isPending && <p className="muted">Loading regulators…</p>}
      {query.isError && (
        <p className="error-text" role="alert">
          {errMessage(query.error, 'Failed to load regulators.')}
        </p>
      )}
      {!query.isPending && !query.isError && regulators.length === 0 && (
        <p className="muted empty-state">No regulators recorded yet.</p>
      )}

      {regulators.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Kind</Table.HeaderCell>
              <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
              <Table.HeaderCell>Portal</Table.HeaderCell>
              <Table.HeaderCell>Lookup</Table.HeaderCell>
              <Table.HeaderCell>Phone</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {regulators.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell tone="strong">{r.name}</Table.Cell>
                <Table.Cell>{regulatorKindLabel(r.kind)}</Table.Cell>
                <Table.Cell>{r.jurisdiction_name ?? r.jurisdiction_code ?? `#${r.jurisdiction_id}`}</Table.Cell>
                <Table.Cell>
                  {r.portal_url
                    ? <a href={r.portal_url} target="_blank" rel="noreferrer">Portal ↗</a>
                    : '—'}
                </Table.Cell>
                <Table.Cell>
                  {r.lookup_url
                    ? <a href={r.lookup_url} target="_blank" rel="noreferrer">Lookup ↗</a>
                    : '—'}
                </Table.Cell>
                <Table.Cell tone="muted">{r.phone ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
}

// =============================================================================
// Training certs section — company-held instructor / school / facility certs
// =============================================================================

function TrainingCertsSection({
  jurisdictionName,
}: {
  jurisdictionName: (id: number) => string;
}): JSX.Element {
  const canWrite = can('compliance.write');
  const query = useTrainingCerts();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ComplianceTrainingCert | null>(null);

  const certs = query.data?.training_certs ?? [];

  return (
    <section className="card compliance-card">
      <header className="card-header">
        <div>
          <h3>Training certifications</h3>
          <p className="page-sub">Company-held instructor, school and firearms-facility credentials.</p>
        </div>
        {canWrite && (
          <Button variant="ghost" className="btn-sm" onClick={() => setCreateOpen(true)}>
            Add training cert
          </Button>
        )}
      </header>

      {query.isPending && <p className="muted">Loading training certs…</p>}
      {query.isError && (
        <p className="error-text" role="alert">
          {errMessage(query.error, 'Failed to load training certs.')}
        </p>
      )}
      {!query.isPending && !query.isError && certs.length === 0 && (
        <p className="muted empty-state">No training certifications recorded yet.</p>
      )}

      {certs.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Kind</Table.HeaderCell>
              <Table.HeaderCell>Number</Table.HeaderCell>
              <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
              <Table.HeaderCell>Holder</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Document</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {certs.map((c) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="strong">{c.name}</Table.Cell>
                <Table.Cell>{trainingCertKindLabel(c.kind)}</Table.Cell>
                {c.number ? <Table.Cell tone="mono">{c.number}</Table.Cell> : <Table.Cell>—</Table.Cell>}
                <Table.Cell>{c.jurisdiction_name ?? c.jurisdiction_code ?? jurisdictionName(c.jurisdiction_id)}</Table.Cell>
                <Table.Cell>
                  {c.holder_person_id !== null
                    ? <Link to={`/persons/${c.holder_person_id}`}>{c.holder_person_name ?? `Person #${c.holder_person_id}`}</Link>
                    : '—'}
                </Table.Cell>
                <Table.Cell>{fmtDate(c.expires_at)}</Table.Cell>
                <Table.Cell><DocumentAttachment segment="training-certs" id={c.id} documentPath={c.document_path ?? null} /></Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button variant="ghost" className="btn-sm" onClick={() => setEditing(c)}>
                      Edit
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <TrainingCertModal mode="create" onClose={() => setCreateOpen(false)} />}
      {editing && <TrainingCertModal mode="edit" cert={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

interface TrainingCertModalProps {
  mode: 'create' | 'edit';
  cert?: ComplianceTrainingCert;
  onClose: () => void;
}

function TrainingCertModal({ mode, cert, onClose }: TrainingCertModalProps): JSX.Element {
  const create = useCreateTrainingCert();
  const update = useUpdateTrainingCert();
  const busy = create.isPending || update.isPending;

  const [name, setName] = useState(cert?.name ?? '');
  const [kind, setKind] = useState<TrainingCertKind>(cert?.kind ?? 'instructor');
  const [number, setNumber] = useState(cert?.number ?? '');
  const [jurisdictionId, setJurisdictionId] = useState(
    cert ? String(cert.jurisdiction_id) : '',
  );
  const [holderPersonId, setHolderPersonId] = useState(
    cert?.holder_person_id !== null && cert?.holder_person_id !== undefined
      ? String(cert.holder_person_id)
      : '',
  );
  const [issuedAt, setIssuedAt] = useState<number | null>(cert?.issued_at ?? null);
  const [expiresAt, setExpiresAt] = useState<number | null>(cert?.expires_at ?? null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (name.trim() === '') {
      setError('Name is required.');
      return;
    }
    const jid = Number(jurisdictionId);
    if (mode === 'create' && (!Number.isInteger(jid) || jid <= 0)) {
      setError('A jurisdiction id is required.');
      return;
    }
    const holder = holderPersonId.trim() === '' ? undefined : Number(holderPersonId);
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          jurisdiction_id: jid,
          kind,
          name: name.trim(),
          ...(number.trim() ? { number: number.trim() } : {}),
          ...(holder !== undefined ? { holder_person_id: holder } : {}),
          ...(issuedAt !== null ? { issued_at: issuedAt } : {}),
          ...(expiresAt !== null ? { expires_at: expiresAt } : {}),
        });
        toast.success('Training cert created.');
      } else if (cert) {
        await update.mutateAsync({
          id: cert.id,
          jurisdiction_id: jid,
          kind,
          name: name.trim(),
          number: number.trim() ? number.trim() : null,
          holder_person_id: holder ?? null,
          issued_at: issuedAt,
          expires_at: expiresAt,
        });
        toast.success('Training cert updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the training cert.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Add training cert' : 'Edit training cert'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="training-cert-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="training-cert-form" className="form-grid" onSubmit={onSubmit}>
        <FormRow>
          <FormField label="Name" required>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={busy}
              required
              autoFocus
            />
          </FormField>
          <FormField label="Kind">
            <select
              className="form-input"
              value={kind}
              onChange={(e) => setKind(e.target.value as TrainingCertKind)}
              disabled={busy}
            >
              {TRAINING_CERT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Number">
            <Input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
          <FormField label="Jurisdiction id" required>
            <Input
              type="number"
              value={jurisdictionId}
              onChange={(e) => setJurisdictionId(e.target.value)}
              min={1}
              disabled={busy}
              required
            />
          </FormField>
        </FormRow>
        <FormField label="Holder person id">
          <Input
            type="number"
            value={holderPersonId}
            onChange={(e) => setHolderPersonId(e.target.value)}
            min={1}
            disabled={busy}
            placeholder="Optional — the person who holds this credential"
          />
        </FormField>
        <FormRow>
          <FormField label="Issued">
            <DateInput value={issuedAt} onChange={setIssuedAt} disabled={busy} />
          </FormField>
          <FormField label="Expires">
            <DateInput value={expiresAt} onChange={setExpiresAt} disabled={busy} />
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}
